import { configureStore } from '@reduxjs/toolkit';
import { authSlice } from '@/store/slices/authSlice';
import { themeSlice } from '@/store/slices/themeSlice';
import { loadingSlice } from '@/store/slices/loadingSlice';
import { analyticsSlice } from '@/store/slices/analyticsSlice';
import feedReducer from '@/store/slices/feedSlice';
import commentReducer from '@/store/slices/commentSlice';
import applicationsReducer from '@/store/slices/applicationsSlice';
import jobCategoryReducer from '@/store/slices/jobCategorySlice';
import companyReducer from '@/store/slices/companySlice';
import followReducer from '@/store/slices/followSlice';
import folderReducer from '@/store/slices/folderSlice';
import knowledgeBaseReducer from '@/store/slices/knowledgeBaseSlice';
import eventReducer from '@/store/slices/eventSlice';

export const store = configureStore({
  reducer: {
    auth: authSlice.reducer,
    theme: themeSlice.reducer,
    loading: loadingSlice.reducer,
    analytics: analyticsSlice.reducer,
    feed: feedReducer,
    comments: commentReducer,
    applications: applicationsReducer,
    jobCategory: jobCategoryReducer,
    company: companyReducer,
    follow: followReducer,
    folders: folderReducer,
    knowledgeBase: knowledgeBaseReducer,
    events: eventReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
        ignoredPaths: ['follow.followingUsers', 'follow.pendingRequests'],
      },
    }),
});

// Re-export types and hooks
export type { RootState, AppDispatch } from '@/store/types';
export { useAppDispatch, useAppSelector } from '@/store/types';

// Export actions and async thunks
export { toggleTheme, setTheme, setPrimaryColor, setSecondaryColor, setCustomColors } from '@/store/slices/themeSlice';
export { clearError, setUser, setToken, clearAuth, loginUser, fetchProfile, logoutUser } from '@/store/slices/authSlice';
export { setLoading, setBackdropLoading, showLoader, hideLoader } from '@/store/slices/loadingSlice';
export {
  fetchAssessmentAttempts,
  fetchAttemptQuestionAnalytics,
  fetchAssessmentStatistics,
  fetchAnalyticsDashboard,
  exportAssessmentAttempts,
  analyticsSlice
} from '@/store/slices/analyticsSlice';
export {
  fetchFeedTypes,
  fetchFeeds,
  loadMoreFeeds,
  createFeed,
  deleteFeed,
  toggleFeedLike,
  getFeedLikes,
  updateFeedStats,
  fetchMyFeeds,
  clearError as clearFeedError,
  resetFeedState,
  setCreating
} from '@/store/slices/feedSlice';
export {
  createComment,
  fetchCommentsByFeedId,
  loadMoreComments,
  updateComment,
  deleteComment,
  clearError as clearCommentError,
  resetCommentState,
  clearComments
} from '@/store/slices/commentSlice';
export {
  fetchApplicationsByFeedId,
  fetchMyApplications,
  clearError as clearApplicationsError,
  clearMyApplicationsError,
  resetApplicationsState,
  setFeedDetails,
  clearApplications,
  clearMyApplications,
  updateApplicationStatus
} from '@/store/slices/applicationsSlice';
export {
  fetchJobCategories,
  fetchJobCategory,
  createJobCategory,
  updateJobCategory,
  deleteJobCategory,
  clearError as clearJobCategoryError,
  resetJobCategoryState,
  setCurrentJobCategory,
  clearJobCategories
} from '@/store/slices/jobCategorySlice';
export {
  fetchCompanies,
  fetchCompany,
  createCompany,
  updateCompany,
  deleteCompany,
  clearError as clearCompanyError,
  resetCompanyState,
  setCurrentCompany,
  clearCompanies
} from '@/store/slices/companySlice';
export {
  followUser,
  unfollowUser,
  cancelFollowRequest,
  setFollowingStatus,
  setPendingStatus,
  setSuggestions,
  setSearchResults,
  clearError as clearFollowError
} from '@/store/slices/followSlice';
export {
  fetchFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  clearError as clearFolderError,
  resetFolderState,
  setCurrentFolder
} from '@/store/slices/folderSlice';
export {
  fetchKnowledgeBaseLinks,
  createKnowledgeBaseLink,
  updateKnowledgeBaseLink,
  deleteKnowledgeBaseLink,
  clearError as clearKnowledgeBaseError,
  resetKnowledgeBaseState,
  setCurrentLink,
  setUploading
} from '@/store/slices/knowledgeBaseSlice';
export {
  fetchEvents,
  fetchEvent,
  createEvent,
  updateEvent,
  deleteEvent,
  enrollEvent,
  markEventInactive,
  clearError as clearEventError,
  resetEventState,
  setCurrentEvent,
  clearEvents,
} from '@/store/slices/eventSlice';